if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        this.initPaginationButtons();
        if (!this.elements.thumbnails) return;

        this.initThumbnailButtons();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initThumbnailButtons() {
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button || button.dataset.mediaGalleryBound === 'true') return;

          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          button.dataset.mediaGalleryBound = 'true';
        });
      }

      initPaginationButtons() {
        this.querySelectorAll('[data-product-media-pagination] .slider-counter__link').forEach((button) => {
          if (button.dataset.mediaGalleryBound === 'true') return;

          button.addEventListener('click', (event) => {
            event.preventDefault();
            const mediaId = event.currentTarget.dataset.mediaId;
            const mediaItem = this.getVisibleMediaItems().find((item) => item.dataset.mediaId === mediaId);
            if (!mediaItem) return;

            this.setActiveMedia(mediaItem.dataset.mediaId, false);
          });
          button.dataset.mediaGalleryBound = 'true';
        });
      }

      getVisibleMediaItems() {
        return Array.from(this.elements.viewer.querySelectorAll('[data-media-id]')).filter((item) => !item.hidden);
      }

      setActivePaginationDot(mediaItem) {
        const paginationButtons = Array.from(
          this.querySelectorAll('[data-product-media-pagination] .slider-counter__link')
        );
        if (!paginationButtons.length || !mediaItem) return;

        paginationButtons.forEach((button) => {
          const isActive = button.dataset.mediaId === mediaItem.dataset.mediaId;
          const wasActive = button.classList.contains('slider-counter__link--active');
          button.classList.toggle('slider-counter__link--active', isActive);
          button.toggleAttribute('aria-current', isActive);

          if (isActive && !wasActive) {
            button.classList.remove('product__media-pagination-dot--animate');
            void button.offsetWidth;
            button.classList.add('product__media-pagination-dot--animate');
          } else if (!isActive) {
            button.classList.remove('product__media-pagination-dot--animate');
          }
        });
      }

      onSlideChanged(event) {
        const currentElement = event.detail.currentElement;
        if (!currentElement) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === currentElement);
        });
        this.setActivePaginationDot(currentElement);

        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const requestedMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        const activeMedia =
          (requestedMedia && !requestedMedia.hidden ? requestedMedia : null) ||
          this.elements.viewer.querySelector('[data-media-id]:not([hidden])') ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (this.scrollDrawerToMedia()) return;

          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.setActivePaginationDot(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail || activeThumbnail.hidden) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      scrollDrawerToMedia() {
        const drawerBody = this.closest('.product-drawer__body');
        if (!drawerBody) return false;

        const drawerPanel = drawerBody.closest('.product-drawer__panel');
        const drawerScrollContainer =
          drawerPanel && window.matchMedia('(max-width: 989px)').matches ? drawerPanel : drawerBody;
        const mediaTop =
          this.getBoundingClientRect().top -
          drawerScrollContainer.getBoundingClientRect().top +
          drawerScrollContainer.scrollTop;

        drawerScrollContainer.scrollTo({ top: Math.max(mediaTop - 16, 0), behavior: 'smooth' });
        return true;
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        this.pauseOtherDrawerMedia(activeItem);
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      pauseOtherDrawerMedia(activeItem) {
        const drawerContent = this.closest('.product-drawer__content');
        if (!drawerContent) {
          window.pauseAllMedia();
          return;
        }

        drawerContent.querySelectorAll('.js-youtube').forEach((video) => {
          if (!activeItem.contains(video)) {
            video.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          }
        });
        drawerContent.querySelectorAll('.js-vimeo').forEach((video) => {
          if (!activeItem.contains(video)) video.contentWindow?.postMessage('{"method":"pause"}', '*');
        });
        drawerContent.querySelectorAll('video').forEach((video) => {
          if (!activeItem.contains(video)) video.pause();
        });
        drawerContent.querySelectorAll('product-model').forEach((model) => {
          if (!activeItem.contains(model) && model.modelViewerUI) model.modelViewerUI.pause();
        });
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
